<template>
  <div
    class="app-border-card-calss bg-white/70! dark:bg-[var(--p-primary-950)]/60! backdrop-blur-2xl h-full flex flex-col border-l border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)]"
  >
    <div class="px-4 py-3 dark:border-[var(--p-surface-700)] flex items-center justify-between">
      <label class="text-sm font-semibold text-[var(--p-text-color)]">
        {{ t('whatsapp.contact_info.title') }}
      </label>
      <button
        @click="$emit('close')"
        class="p-2 rounded-full cursor-pointer hover:bg-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)] transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-[var(--p-text-muted-color)]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <div v-if="loading && !contactProfile" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3">
        <div
          class="w-8 h-8 border-4 border-[var(--p-primary-color)] border-t-transparent rounded-full animate-spin"
        />
        <span class="text-sm text-[var(--p-text-muted-color)]">
          {{ t('whatsapp.contact_info.loading') }}
        </span>
      </div>
    </div>

    <div v-else-if="contactProfile" class="flex-1 overflow-y-auto">
      <div class="p-4">
        <div class="rounded-2xl px-5">
          <div
            class="flex items-center gap-3 border-b border-[var(--p-surface-100)] pb-4 dark:border-[var(--p-surface-800)]"
          >
            <AppAvatar
              size="large"
              shape="circle"
              :label="getInitials(contactProfile.name)"
              v-bind="contactAvatarImage ? { image: contactAvatarImage } : {}"
              class="!h-12 !w-12 !rounded-full font-bold shadow-md shadow-[var(--p-primary-color)] text-sm! dark:bg-[var(--p-primary-color)]/20!"
            />
            <div class="min-w-0">
              <h4 class="truncate text-sm font-semibold text-[var(--p-text-color)]">
                {{ contactProfile.name }}
              </h4>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  class="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-500 dark:bg-blue-950/50 dark:text-blue-300"
                >
                  {{ t('whatsapp.contact_message.contact') }}
                </span>
                <span
                  v-if="contactData?.status"
                  class="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                  :class="getStatusClass(contactData.status)"
                >
                  {{ getStatusLabel(contactData.status) }}
                </span>
              </div>
            </div>
          </div>

          <div class="space-y-5 pt-5">
            <div
              v-if="loading"
              class="app-card-glass backdrop-blur-sm rounded-xl border  p-3 text-sm text-[var(--p-text-muted-color)]"
            >
              {{ t('whatsapp.contact_info.loading_additional') }}
            </div>

            <div
              v-if="error"
              class="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
            >
              <div class="flex items-start justify-between gap-3">
                <span>{{ error }}</span>
                <button
                  class="font-semibold text-red-700 dark:text-red-200"
                  @click="$emit('retry')"
                >
                  {{ t('whatsapp.contact_info.retry') }}
                </button>
              </div>
            </div>

            <section v-if="contactDetails.length">
              <h5 class="mb-3 text-xs font-bold uppercase text-[var(--p-text-muted-color)]">
                {{ t('whatsapp.contact_info.title') }}
              </h5>
              <div class="grid grid-cols-1 gap-4">
                <div v-for="detail in contactDetails" :key="detail.id" class="min-w-0">
                  <label class="text-[11px] font-bold uppercase text-[var(--p-text-muted-color)]">
                    {{ detail.label }}
                  </label>
                  <p class="mt-1 break-words text-xs font-medium text-[var(--p-text-color)]">
                    {{ detail.value }}
                  </p>
                </div>
              </div>
            </section>

            <section
              v-if="contactMeta.length"
              class="border-t border-[var(--p-surface-100)] pt-4 dark:border-[var(--p-surface-800)]"
            >
              <h5 class="mb-3 text-xs font-bold uppercase text-[var(--p-text-muted-color)]">
                {{ t('whatsapp.contact_info.campaign') }}
              </h5>
              <div class="space-y-2">
                <div
                  v-for="item in contactMeta"
                  :key="item.id"
                  class="flex min-h-9 items-center justify-between gap-3 rounded-xl bg-[var(--p-surface-50)]/50 px-3 py-2 dark:bg-[var(--p-primary-800)]/20"
                >
                  <span class="text-xs font-semibold text-[var(--p-text-muted-color)]">
                    {{ item.label }}
                  </span>
                  <span class="text-right text-xs font-medium text-[var(--p-text-color)]">
                    {{ item.value }}
                  </span>
                </div>
              </div>
            </section>

            <section
              v-if="contactData?.custom_fields && contactData.custom_fields.length > 0"
              class="border-t border-[var(--p-surface-100)] pt-4 dark:border-[var(--p-surface-800)]"
            >
              <h5 class="mb-3 text-xs font-bold uppercase text-[var(--p-text-muted-color)]">
                {{ t('whatsapp.contact_info.custom_fields') }}
              </h5>
              <div class="space-y-2">
                <div
                  v-for="field in contactData.custom_fields"
                  :key="field.id"
                  class="flex min-h-9 items-center justify-between gap-3 rounded-xl bg-[var(--p-surface-50)] px-3 py-2 dark:bg-[var(--p-surface-800)]"
                >
                  <span class="text-xs font-semibold text-[var(--p-text-muted-color)]">
                    {{ field.name || t('whatsapp.contact_info.field') }}
                  </span>
                  <span class="text-right text-xs font-bold text-[var(--p-text-color)]">
                    {{ field.value || t('whatsapp.contact_info.no_value') }}
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex-1 flex items-center justify-center p-4">
      <div class="text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-12 w-12 mx-auto mb-3 text-[var(--p-text-muted-color)]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        <p class="text-sm text-[var(--p-text-muted-color)]">
          {{ t('whatsapp.contact_info.no_information') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import type { ContactDto } from '@/services/contact/interfaces/contact.interface'
import { ContactStatus } from '@/services/contact/enums/contact-status.enum'
import { parseUtcDate } from '../../composables/useTimeFormatter'
import { formatUsername } from '../../services/contactIdentity'
import { useI18n } from 'vue-i18n'

interface Props {
  contactData: ContactDto | null
  campaignName?: string | null
  contactName?: string | null
  contactPhone?: string | null
  contactUsername?: string | null
  maskNumber?: boolean
  loading?: boolean
  error?: string | null
}

const props = defineProps<Props>()
const { t, locale } = useI18n()
defineEmits<{
  close: []
  retry: []
}>()

const contactProfile = computed(() => {
  if (props.contactData) return props.contactData
  if (!props.contactName && !props.contactPhone && !props.contactUsername) return null

  return {
    name: props.contactName || t('whatsapp.main.unknown_contact'),
    phone: props.contactPhone || '-',
  }
})

// Un cliente con username puede ocultar su teléfono: se dice así en vez de "-".
const phoneHiddenByCustomer = computed(
  () => !props.contactData && !props.contactPhone && Boolean(props.contactUsername),
)

const contactDetails = computed(() =>
  [
    {
      id: 'phone',
      label: t('general.phone'),
      value: phoneHiddenByCustomer.value
        ? t('whatsapp.contact_info.phone_hidden')
        : maskPhoneNumber(contactProfile.value?.phone),
    },
    {
      id: 'username',
      label: t('whatsapp.contact_info.username'),
      value: formatUsername(props.contactUsername),
    },
    {
      id: 'email',
      label: t('general.email'),
      value: props.contactData?.email,
    },
  ].filter((detail) => Boolean(detail.value)),
)

const maskPhoneNumber = (phone?: string | null): string | undefined => {
  if (!phone) return undefined
  if (!props.maskNumber) return phone

  return phone.slice(0, 4) + '*'.repeat(Math.max(0, phone.length - 4))
}

const contactMeta = computed(() =>
  [
    {
      id: 'campaign',
      label: t('whatsapp.contact_info.campaign'),
      value: props.contactData?.campaign_id && props.campaignName ? props.campaignName : '',
    },
    {
      id: 'origin',
      label: t('whatsapp.contact_info.origin'),
      value: props.contactData?.origin,
    },
    {
      id: 'created_at',
      label: t('general.created_at'),
      value: props.contactData?.created_at ? formatDate(props.contactData.created_at) : '',
    },
  ].filter((item) => Boolean(item.value)),
)

const contactAvatarImage = computed(() => {
  if (!props.contactData) return ''

  const contact = props.contactData as ContactDto &
    Partial<
      Record<
        'avatar' | 'photo' | 'image' | 'image_url' | 'profile_image' | 'profileImage' | 'picture',
        string | null
      >
    >

  return (
    contact.avatar ||
    contact.photo ||
    contact.image ||
    contact.image_url ||
    contact.profile_image ||
    contact.profileImage ||
    contact.picture ||
    ''
  )
})

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean)

  if (!words.length) return '?'

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
}

const getStatusClass = (status: ContactStatus): string => {
  const classes: Record<ContactStatus, string> = {
    [ContactStatus.PENDING]:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    [ContactStatus.PLACED]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    [ContactStatus.DIALING]:
      'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
    [ContactStatus.QUEUED]: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    [ContactStatus.CONNECTED]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    [ContactStatus.SUCCESS]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    [ContactStatus.WAITING]:
      'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    [ContactStatus.FAILED]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    [ContactStatus.NOANSWER]: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    [ContactStatus.ABANDONED]: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    [ContactStatus.DELETED]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    [ContactStatus.NOANSWER_AMD]: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  }
  return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
}

const getStatusLabel = (status: ContactStatus): string => {
  const labels: Record<ContactStatus, string> = {
    [ContactStatus.PENDING]: t('whatsapp.contact_info.status_pending'),
    [ContactStatus.PLACED]: t('whatsapp.contact_info.status_placed'),
    [ContactStatus.DIALING]: t('whatsapp.contact_info.status_dialing'),
    [ContactStatus.QUEUED]: t('whatsapp.contact_info.status_queued'),
    [ContactStatus.CONNECTED]: t('whatsapp.contact_info.status_connected'),
    [ContactStatus.SUCCESS]: t('whatsapp.contact_info.status_success'),
    [ContactStatus.WAITING]: t('whatsapp.contact_info.status_waiting'),
    [ContactStatus.FAILED]: t('whatsapp.contact_info.status_failed'),
    [ContactStatus.NOANSWER]: t('whatsapp.contact_info.status_no_answer'),
    [ContactStatus.ABANDONED]: t('whatsapp.contact_info.status_abandoned'),
    [ContactStatus.DELETED]: t('whatsapp.contact_info.status_deleted'),
    [ContactStatus.NOANSWER_AMD]: t('whatsapp.contact_info.status_no_answer_amd'),
  }
  return labels[status] || status
}

const formatDate = (dateString: string): string => {
  const date = parseUtcDate(dateString)
  if (Number.isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat(locale.value === 'en' ? 'en-US' : 'es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
</script>
